// =============================================================================
// Phase 1 SOC / Wazuh Integration: Security Page
// =============================================================================

import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Radar, Settings, RefreshCw } from 'lucide-react';
import { DiscoveryList } from '@/components/security/DiscoveryList';
import { WazuhConfigForm } from '@/components/security/WazuhConfigForm';
import { useSecurityStore } from '@/stores/securityStore';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

// TODO Phase 2: Add "Status" tab for endpoint status dashboard
// TODO Phase 2: Add "Alerts" tab for Wazuh alerts -> incidents pipeline

export default function SecurityPage() {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState('discovery');
  const { 
    fetchWazuhConfig, 
    fetchDiscoveries, 
    triggerSync, 
    syncing,
    wazuhConfig,
    configLoading,
  } = useSecurityStore();
  
  const orgId = profile?.org_id;
  const isAdmin = profile?.role === 'admin';
  
  // Load data on mount
  useEffect(() => {
    if (orgId) {
      fetchWazuhConfig(orgId);
      fetchDiscoveries(orgId);
    }
  }, [orgId, fetchWazuhConfig, fetchDiscoveries]);
  
  // Handle manual sync
  const handleSync = async () => {
    if (!orgId) return;
    
    try {
      const result = await triggerSync(orgId);
      if (result.success) {
        toast.success(`Sync completed: ${result.discoveries_created} new, ${result.discoveries_updated} updated`);
      } else {
        toast.error(`Sync failed: ${result.errors.join(', ')}`);
      }
    } catch (err) {
      toast.error('Failed to sync with Wazuh');
    }
  };
  
  return (
    <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Security</h1>
            <p className="text-muted-foreground mt-1">
              Network discovery and endpoint status monitoring
            </p>
          </div>
          
          {wazuhConfig && (
            <Button 
              onClick={handleSync} 
              disabled={syncing || configLoading}
              variant="outline"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Now'}
            </Button>
          )}
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="discovery" className="flex items-center gap-2">
              <Radar className="h-4 w-4" />
              Discovery
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="configuration" className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Configuration
              </TabsTrigger>
            )}
          </TabsList>
          
          <TabsContent value="discovery" className="mt-6">
            <DiscoveryList />
          </TabsContent>
          
          {isAdmin && (
            <TabsContent value="configuration" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Wazuh Configuration</CardTitle>
                  <CardDescription>
                    Configure the connection to your Wazuh SIEM/XDR instance.
                    {wazuhConfig?.last_sync_at && (
                      <span className="block mt-1 text-xs">
                        Last sync: {new Date(wazuhConfig.last_sync_at).toLocaleString()}
                        {wazuhConfig.last_sync_status === 'error' && (
                          <span className="text-danger ml-2">
                            (Error: {wazuhConfig.last_sync_error})
                          </span>
                        )}
                      </span>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <WazuhConfigForm />
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
    </div>
  );
}
